// LicenseSettings.tsx
// Platform license status and subscription management.
// Tier A (Developer / System Admin) only.
// Satisfies: Supplementary Architecture Brief D3
//
// Shows: current plan, feature flags, resource limits, valid-until date.
// Includes a "Simulate license state" demo tool so the developer can show
// clients what happens when a license lapses (Read-Only Mode).

import { useState } from 'react'
import { useAuthStore } from '../stores/auth.store'
import {
  MOCK_LICENSE_TOKEN, planLabel, isLicenseValid, isReadOnly,
  daysUntilExpiry, FEATURES, PLAN_FEATURES, PLAN_LIMITS,
  type LicenseToken, type LicensePlan, type LicenseStatus,
} from '../utils/licenseConfig'

// ── Re-export a frontend-safe version of the license service ──────────────
// The actual API service lives in the Worker. For the Settings UI we use
// the same types and mock token directly — no network call needed for the
// prototype. In production the UI calls GET /api/system/license.

const API_BASE = (import.meta as any).env?.VITE_API_URL ?? 'https://trafficprocure-api.dirlayo69.workers.dev'

// ── Types ──────────────────────────────────────────────────────────────────

type SimulatedState = 'real' | 'expired' | 'suspended' | 'trial' | 'expiring-soon'

const SIMULATED_TOKENS = {
  real:           MOCK_LICENSE_TOKEN,
  trial:          { ...MOCK_LICENSE_TOKEN, status: 'trial' as const, plan: 'starter' as const, features: PLAN_FEATURES.starter, maxUsers: 3, maxJobs: 5, validUntil: new Date(Date.now() + 14*86400000).toISOString().split('T')[0] },
  expiring_soon:  { ...MOCK_LICENSE_TOKEN, validUntil: new Date(Date.now() + 5*86400000).toISOString().split('T')[0] },
  suspended:      { ...MOCK_LICENSE_TOKEN, status: 'suspended' as const },
  expired:        { ...MOCK_LICENSE_TOKEN, status: 'expired' as const, validUntil: new Date(Date.now() - 3*86400000).toISOString().split('T')[0] },
}

// Correct key mapping
const SIM_KEYS: Record<SimulatedState, keyof typeof SIMULATED_TOKENS> = {
  'real':           'real',
  'expired':        'expired',
  'suspended':      'suspended',
  'trial':          'trial',
  'expiring-soon':  'expiring_soon' as any,
}

const PLAN_COLORS: Record<LicensePlan, { bg: string; text: string; border: string; badge: string }> = {
  starter:      { bg: 'bg-gray-100',   text: 'text-gray-700',  border: 'border-gray-300',  badge: 'bg-gray-200 text-gray-700' },
  professional: { bg: 'bg-blue-50',    text: 'text-blue-700',  border: 'border-blue-300',  badge: 'bg-blue-600 text-white' },
  business:     { bg: 'bg-purple-50',  text: 'text-purple-700',border: 'border-purple-300',badge: 'bg-purple-600 text-white' },
  enterprise:   { bg: 'bg-amber-50',   text: 'text-amber-800', border: 'border-amber-300', badge: 'bg-amber-600 text-white' },
}

const STATUS_COLORS: Record<LicenseStatus, { bg: string; text: string; dot: string }> = {
  active:    { bg: 'bg-green-50',  text: 'text-green-700',  dot: 'bg-green-500'  },
  trial:     { bg: 'bg-blue-50',   text: 'text-blue-700',   dot: 'bg-blue-500'   },
  suspended: { bg: 'bg-amber-50',  text: 'text-amber-700',  dot: 'bg-amber-500'  },
  expired:   { bg: 'bg-red-50',    text: 'text-red-700',    dot: 'bg-red-500'    },
}

const ALL_FEATURES = [
  { key: FEATURES.WHS_FULL,       label: 'Full WHS module',              desc: 'All 16 WHS document types, AI agent A04' },
  { key: FEATURES.AI_AGENTS,      label: 'AI Agent fleet',               desc: 'A04 WHS Officer, A05 Supplier Intel, A09 QA, A13 CA' },
  { key: FEATURES.XERO,           label: 'Xero integration',             desc: 'OAuth 2.0, invoice push, bank reconciliation' },
  { key: FEATURES.STP,            label: 'Single Touch Payroll',         desc: 'STP Phase 2 lodgement via payroll intermediary' },
  { key: FEATURES.MOBILE,         label: 'Mobile crew management',       desc: 'Push notifications, crew app, field data capture' },
  { key: FEATURES.SUPPLIER_INTEL, label: 'Supplier Intelligence (A05)',  desc: 'Product spec compliance, lead time analysis, ranking' },
  { key: FEATURES.CUSTOM_BRAND,   label: 'Custom branding',              desc: 'Logo, colours, document headers per entity' },
  { key: FEATURES.MULTI_ENTITY,   label: 'Multi-entity',                 desc: 'Subsidiary companies and divisions within one account' },
  { key: FEATURES.API_ACCESS,     label: 'Direct API access',            desc: 'REST API for third-party integrations' },
]

// ── Component ──────────────────────────────────────────────────────────────

export function LicenseSettings() {
  const { isTier } = useAuthStore()
  const isAdmin = isTier('A_DEVELOPER')

  const [simState, setSimState] = useState<SimulatedState>('real')
  const [loadedToken, setLoadedToken] = useState<LicenseToken | null>(null)
  const [loading, setLoading] = useState(false)

  // Derive active token (simulated or loaded from API)
  const token: LicenseToken = (() => {
    const key = SIM_KEYS[simState] as keyof typeof SIMULATED_TOKENS
    return simState !== 'real' ? (SIMULATED_TOKENS[key] ?? SIMULATED_TOKENS.real) : (loadedToken ?? MOCK_LICENSE_TOKEN)
  })()

  const valid    = isLicenseValid(token)
  const readOnly = isReadOnly(token)
  const days     = daysUntilExpiry(token)
  const planC    = PLAN_COLORS[token.plan]
  const statusC  = STATUS_COLORS[token.status]
  const isMock   = !loadedToken

  const loadFromAPI = async () => {
    setLoading(true)
    try {
      const res  = await fetch(`${API_BASE}/api/system/license`)
      const data = await res.json()
      if (data.ok && data.license) setLoadedToken(data.license as LicenseToken)
    } catch {
      // API unreachable — prototype mock stays active
    }
    setLoading(false)
  }

  const fmt = (s: string) => new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })

  if (!isAdmin) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-6 text-center">
        <div className="font-semibold text-amber-900 mb-1">Administrator access required</div>
        <p className="text-sm text-amber-700">License information is restricted to Tier A (Developer / System Admin).</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-3xl">

      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900">License &amp; subscription</h2>
        <p className="text-sm text-gray-500 mt-1">Current subscription status for this TrafficProcure installation. License tokens are issued by the TrafficProcure License Authority and renewed automatically while the subscription is active.</p>
      </div>

      {/* Prototype notice */}
      {isMock && simState === 'real' && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-800 flex items-start gap-3">
          <div className="flex-shrink-0 font-bold text-lg">ℹ</div>
          <div>
            <strong>Prototype mode.</strong> No <code className="text-xs bg-blue-100 px-1 rounded">LICENSE_TOKEN</code> secret is set. The platform is showing the default professional-plan mock token. In production, set <code className="text-xs bg-blue-100 px-1 rounded">LICENSE_TOKEN</code> as a Wrangler secret using the signed JWT issued by the TrafficProcure License Authority.
            <button onClick={loadFromAPI} disabled={loading} className="ml-3 text-xs underline hover:no-underline">
              {loading ? 'Loading…' : 'Load from API'}
            </button>
          </div>
        </div>
      )}

      {/* Read-only mode banner */}
      {readOnly && (
        <div className="bg-red-50 border-2 border-red-300 rounded-xl px-5 py-4 flex items-start gap-3">
          <div className="text-2xl flex-shrink-0">🔒</div>
          <div>
            <div className="font-bold text-red-800 text-base">Platform is in Read-Only Mode</div>
            <p className="text-sm text-red-700 mt-1">
              {token.status === 'expired'
                ? `Subscription expired ${Math.abs(days)} day${Math.abs(days) !== 1 ? 's' : ''} ago. Renew to restore full access.`
                : token.status === 'suspended'
                ? 'This account has been suspended. Contact TrafficProcure support to restore access.'
                : 'License is not valid. Contact TrafficProcure to renew your subscription.'}
            </p>
            <p className="text-xs text-red-600 mt-2">Users can view all their data but cannot create, edit, submit, or export until the subscription is renewed.</p>
          </div>
        </div>
      )}

      {/* Plan card */}
      <div className={`border-2 rounded-xl p-5 ${planC.border} ${planC.bg}`}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className={`text-sm font-bold px-3 py-1 rounded-full ${planC.badge}`}>{planLabel(token.plan)} Plan</span>
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1.5 ${statusC.bg} ${statusC.text}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${statusC.dot}`} />
                {token.status.charAt(0).toUpperCase() + token.status.slice(1)}
              </span>
            </div>
            <div className="font-semibold text-gray-900 text-lg">{token.companyName}</div>
            <div className="text-sm text-gray-500 mt-0.5">Tenant: <code className="text-xs bg-white/60 px-1.5 py-0.5 rounded">{token.tenantId}</code></div>
          </div>
          <div className="text-right">
            <div className={`text-2xl font-bold ${days <= 14 && valid ? 'text-amber-700' : days < 0 ? 'text-red-700' : 'text-gray-900'}`}>
              {days < 0 ? `Expired ${Math.abs(days)}d ago` : days === 0 ? 'Expires today' : `${days} days`}
            </div>
            <div className="text-sm text-gray-500">Valid until: {fmt(token.validUntil)}</div>
            <div className="text-xs text-gray-400 mt-1">Issued by: {token.issuedBy}</div>
          </div>
        </div>

        {/* Resource limits */}
        <div className="grid grid-cols-2 gap-3 mt-4">
          {[
            { label: 'Users', limit: token.maxUsers, used: 8 },
            { label: 'Active jobs', limit: token.maxJobs, used: 6 },
          ].map(({ label, limit, used }) => {
            const pct = limit ? Math.round(used / limit * 100) : 0
            const barColor = pct >= 90 ? 'bg-red-500' : pct >= 75 ? 'bg-amber-500' : 'bg-green-500'
            return (
              <div key={label} className="bg-white/70 rounded-lg p-3">
                <div className="flex justify-between text-xs text-gray-600 mb-1.5">
                  <span className="font-medium">{label}</span>
                  <span>{used} / {limit ?? '∞'}</span>
                </div>
                {limit && (
                  <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${barColor}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Feature flags */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="font-medium text-gray-900 mb-4">Features included in this plan</h3>
        <div className="space-y-2">
          {ALL_FEATURES.map(f => {
            const enabled = valid && token.features.includes(f.key)
            return (
              <div key={f.key} className={`flex items-start gap-3 p-3 rounded-lg ${enabled ? 'bg-green-50' : 'bg-gray-50'}`}>
                <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${enabled ? 'bg-green-500' : 'bg-gray-300'}`}>
                  <span className="text-white text-[10px] font-bold">{enabled ? '✓' : '✗'}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-medium ${enabled ? 'text-gray-900' : 'text-gray-400'}`}>{f.label}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{f.desc}</div>
                </div>
                {!enabled && !token.features.includes(f.key) && valid && (
                  <span className="text-[10px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full flex-shrink-0">Upgrade required</span>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Demo tool — Tier A only */}
      <div className="bg-purple-50 border border-purple-200 rounded-xl p-5">
        <h3 className="font-medium text-purple-900 mb-1 flex items-center gap-2">
          <span className="text-purple-600">⚙</span>
          Simulate license state <span className="text-xs text-purple-500 font-normal">(Tier A demo tool — not visible in production)</span>
        </h3>
        <p className="text-xs text-purple-600 mb-3">Demonstrate what the platform looks like under each license state without affecting the real subscription.</p>
        <div className="flex gap-2 flex-wrap">
          {([
            { key: 'real',          label: 'Active (current)' },
            { key: 'trial',         label: 'Trial — 14 days' },
            { key: 'expiring-soon', label: 'Expiring soon — 5 days' },
            { key: 'suspended',     label: 'Suspended' },
            { key: 'expired',       label: 'Expired' },
          ] as const).map(opt => (
            <button key={opt.key} onClick={() => setSimState(opt.key as SimulatedState)}
              className={`text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors ${
                simState === opt.key ? 'bg-purple-700 text-white border-purple-700' : 'bg-white text-purple-700 border-purple-300 hover:bg-purple-50'
              }`}>
              {opt.label}
            </button>
          ))}
        </div>
        {simState !== 'real' && (
          <p className="text-xs text-purple-500 mt-2">Showing simulated state: <strong>{simState}</strong>. The plan card and feature flags above reflect this state.</p>
        )}
      </div>

      {/* Architecture note */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
        <h3 className="font-medium text-gray-900 mb-3 text-sm">How the License Authority works</h3>
        <div className="space-y-2.5 text-xs text-gray-600 leading-relaxed">
          <p>The <strong>TrafficProcure License Authority</strong> is a separate API deployment maintained by the developer. It contains the tenant registry, the JWT signing key, the developer admin panel, and usage telemetry. Clients never have access to it.</p>
          <p>When the platform Worker starts, it contacts the authority using its <code className="bg-gray-200 px-1 rounded">LICENSE_TOKEN</code> secret to obtain a signed 24-hour JWT. The JWT is cached in Worker memory and verified locally on each API request — no network call per request.</p>
          <p>Every 12–24 hours the Worker silently renews the token. If the subscription has lapsed or the tenant is suspended, the authority returns an invalid token. After the cached token expires, the platform enters <strong>Read-Only Mode</strong>: users can view all data but cannot create, edit, submit, or export.</p>
          <p>To apply: set <code className="bg-gray-200 px-1 rounded">LICENSE_TOKEN</code> as a Wrangler secret using the JWT issued by the authority. Redeploying is not required — the Worker reads the new secret on next startup.</p>
        </div>
      </div>

      {/* Plan upgrade matrix */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b bg-gray-50">
          <h3 className="font-medium text-gray-900 text-sm">Plan comparison</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-2.5 text-gray-500 font-semibold uppercase tracking-wide">Feature</th>
                {(['starter','professional','business','enterprise'] as LicensePlan[]).map(p => (
                  <th key={p} className={`px-4 py-2.5 text-center font-semibold ${p === token.plan ? 'text-blue-700 bg-blue-50' : 'text-gray-500'}`}>
                    {planLabel(p)}
                    {p === token.plan && <div className="text-[10px] text-blue-500 font-normal">Current</div>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {ALL_FEATURES.map((f, i) => (
                <tr key={f.key} className={i % 2 ? 'bg-gray-50/50' : ''}>
                  <td className="px-4 py-2.5 text-gray-700 font-medium">{f.label}</td>
                  {(['starter','professional','business','enterprise'] as LicensePlan[]).map(p => (
                    <td key={p} className={`px-4 py-2.5 text-center ${p === token.plan ? 'bg-blue-50/50' : ''}`}>
                      {PLAN_FEATURES[p].includes(f.key)
                        ? <span className="text-green-600 font-bold">✓</span>
                        : <span className="text-gray-300">—</span>}
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="bg-gray-50">
                <td className="px-4 py-2.5 text-gray-700 font-medium">Max users</td>
                {(['starter','professional','business','enterprise'] as LicensePlan[]).map(p => (
                  <td key={p} className={`px-4 py-2.5 text-center text-gray-600 ${p === token.plan ? 'bg-blue-50/50' : ''}`}>
                    {PLAN_LIMITS[p].maxUsers ?? '∞'}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="px-4 py-2.5 text-gray-700 font-medium">Max active jobs</td>
                {(['starter','professional','business','enterprise'] as LicensePlan[]).map(p => (
                  <td key={p} className={`px-4 py-2.5 text-center text-gray-600 ${p === token.plan ? 'bg-blue-50/50' : ''}`}>
                    {PLAN_LIMITS[p].maxJobs ?? '∞'}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
